import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { config } from '../../config.js';
import type { PudoLocation } from '../../db/PudoLocation.js';
import type { ServiceNowTicket } from '../../db/ServiceNowTicket.js';
import * as m from '../../localization/out/messages.js';
import type { MailRecipients } from './_colleague-format.js';
import { type ColleagueMapHandle, createColleagueMap } from './_colleague-map.js';

/**
 * The map view shared by the FE's own Tickets list and the Colleagues list: the tickets that have coordinates,
 * plus the ServiceNow PUDO depots, with a switch to take the depots away (about a hundred pins is dense).
 *
 * Self-contained: it owns the map's lifecycle, so a page only decides WHEN to show it and what to put on it.
 */
export const TicketsMapView = stateful(
  ({
    tickets,
    pudos,
    recipients,
  }: {
    tickets: ServiceNowTicket[];
    pudos: PudoLocation[];
    recipients: MailRecipients | null;
  }) => {
    // Depots are on by default — they're the reason to open the map alongside the tickets.
    const [showPudos, setShowPudos] = useState(true);
    const [mapError, setMapError] = useState<string | null>(null);

    // ── Map lifecycle ──────────────────────────────────────────────────────────────────────
    //
    // Two constraints from functional-lit shape this block:
    //  1. effect bodies run *during* the render pass, and `setState` from there throws
    //     ("You cannot update the state while the component is rendering"), so the map handle lives
    //     in a mutable box rather than in state, and the only `setState` left runs from an async
    //     `.catch` (i.e. after an await, safely outside the render);
    //  2. the container element isn't committed to the DOM yet when the effect body runs, so map
    //     creation is deferred to the next task.
    //
    // The map instance itself survives re-renders — only its pins are refreshed — and is torn down
    // when the view is left.

    // `useState` keeps the same object across renders, which is the closest thing to a ref here.
    const [mapStore] = useState<{
      handle: ColleagueMapHandle | null;
      tickets: ServiceNowTicket[];
      pudos: PudoLocation[];
      recipients: MailRecipients | null;
    }>({ handle: null, tickets: [], pudos: [], recipients: null });

    // Keep the box pointed at the newest data so the async map creation below can paint immediately
    // with whatever has loaded by the time it resolves.
    mapStore.tickets = tickets;
    mapStore.pudos = showPudos ? pudos : [];
    mapStore.recipients = recipients;

    const mapboxToken = config.get('MAPBOX_ACCESS_TOKEN');

    useEffect(() => {
      if (!mapboxToken) return undefined;

      let cancelled = false;
      const timer = setTimeout(() => {
        const container = document.getElementById('tickets-map');
        if (cancelled || !container) return;

        void createColleagueMap(container, mapboxToken)
          .then(created => {
            if (cancelled) {
              created.destroy();
              return;
            }
            mapStore.handle = created;
            created.update(mapStore.tickets, mapStore.pudos, mapStore.recipients);
          })
          .catch(() => {
            if (!cancelled) setMapError(m.colleague_map_unavailable());
          });
      }, 0);

      return () => {
        cancelled = true;
        clearTimeout(timer);
        mapStore.handle?.destroy();
        mapStore.handle = null;
      };
    }, [mapboxToken]);

    // Re-pin whenever the data changes. Safe to run during the render pass: it only touches the map, never
    // component state.
    useEffect(() => {
      mapStore.handle?.update(tickets, mapStore.pudos, mapStore.recipients);
    }, [tickets, pudos, showPudos, recipients?.to, recipients?.cc?.join(',')]);

    return html`
      <div class="flex-1 min-h-0 flex flex-col gap-1">
        <div class="flex items-center justify-between gap-2 flex-wrap">
          <div class="text-xs text-base-content/50">
            ${m.colleague_map_legend({
              tickets: String(tickets.filter(t => typeof t.locationLat === 'number').length),
              pudos: String(showPudos ? pudos.filter(p => typeof p.lat === 'number').length : 0),
            })}
          </div>
          <label class="label cursor-pointer gap-2 py-0">
            <span class="label-text text-xs">${m.colleague_show_pudos()}</span>
            <input
              type="checkbox"
              class="toggle toggle-sm toggle-primary"
              .checked=${showPudos}
              @change=${(e: Event) => setShowPudos((e.target as HTMLInputElement).checked)}
            />
          </label>
        </div>
        ${!mapboxToken
          ? html`<div class="alert alert-warning text-xs py-2">${m.colleague_map_no_token()}</div>`
          : mapError
            ? html`<div class="alert alert-warning text-xs py-2">${mapError}</div>`
            : nothing}
        <!-- Kept mounted (not swapped out on error) so mapbox always has a stable container. -->
        <div id="tickets-map" class="flex-1 min-h-64 rounded-xl border border-base-300 overflow-hidden"></div>
      </div>
    `;
  },
);
